import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import iconAccount from '../assets/img/as-finder-logo-icon.svg';
import { usePasswordValidation } from '../hooks/usePasswordValidation';
import { VALIDATION_MESSAGES } from '../utils/passwordValidation';
import { validateAccountEditForm, getFirstErrorMessage } from '../utils/accountEditValidation';
import '../styles/AccountCreate.css';
import '../styles/button.css';
import '../styles/PasswordValidation.css';

const AccountEditForm = ({ formData, setFormData, onSubmit, isLoading, isEmailEditable = true }) => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [usernameLabel, setUsernameLabel] = useState('ユーザー名');
  
  // フィールドバリデーション状態
  const [fieldErrors, setFieldErrors] = useState({
    username: [],     // ユーザー名のエラーメッセージ配列
    accountname: []   // アカウント名のエラーメッセージ配列
  });
  const [showFieldErrors, setShowFieldErrors] = useState({
    username: false,    // ユーザー名エラーの表示フラグ
    accountname: false  // アカウント名エラーの表示フラグ
  });
  const [touchedFields, setTouchedFields] = useState({
    username: false,    // ユーザー名フィールドがタッチされたか
    accountname: false  // アカウント名フィールドがタッチされたか
  });
  
  // パスワードバリデーション用カスタムフック
  const {
    password,
    confirmPassword,
    passwordErrors,
    isPasswordMatch,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handlePasswordBlur,
    handleConfirmPasswordBlur,
    handlePasswordFocus,
    handleConfirmPasswordFocus,
    validateForm,
    showPasswordErrors,
    showPasswordMatchError,
    setPasswordValue,
    isPasswordSecure,
    isValidating
  } = usePasswordValidation({
    validateOnChange: true,
    showErrorsImmediately: true
  });
  
  useEffect(() => {
    // Get userType from localStorage when component mounts
    const userType = localStorage.getItem('userType');
    
    // Set the appropriate label based on userType
    switch(userType) {
      case 'admin':
        setUsernameLabel('ユーザー名');
        break;
      case 'agency':
        setUsernameLabel('代理店名');
        break;
      case 'solicitor':
        setUsernameLabel('担当者名漢字');
        break;
      default:
        setUsernameLabel('ユーザー名');
    }
  }, []);

  // formDataのパスワードフィールドと同期
  useEffect(() => {
    if (formData.password !== password || formData.confirmPassword !== confirmPassword) {
      setPasswordValue(formData.password || '', formData.confirmPassword || '');
    }
  }, [formData.password, formData.confirmPassword, password, confirmPassword, setPasswordValue]);

  // 指定されたフィールドのバリデーションを実行する関数
  const validateField = (fieldName, value) => {
    const tempFormData = { ...formData, [fieldName]: value };
    const validationResult = validateAccountEditForm(tempFormData, isEmailEditable);
    
    // エラー状態を更新
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: validationResult[fieldName]
    }));
    
    // バリデーション結果を返す（true: 成功, false: 失敗）
    return validationResult[fieldName].length === 0;
  };

  // 入力フィールドの値が変更された時の処理
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // パスワードフィールドの場合は専用のハンドラーを使用
    if (name === 'password') {
      handlePasswordChange(value);
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === 'confirmPassword') {
      handleConfirmPasswordChange(value);
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      // その他のフィールドは通常の処理
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // ユーザー名またはアカウント名の場合、リアルタイムバリデーション実行
      if (name === 'username' || name === 'accountname') {
        validateField(name, value);
        
        // フィールドがタッチされている場合のみエラーを表示
        if (touchedFields[name]) {
          setShowFieldErrors(prev => ({
            ...prev,
            [name]: true
          }));
        }
      }
    }
    
    // 入力時にフォーム全体のエラーメッセージをクリア
    if (error) {
      setError(null);
    }
  };

  // フィールドからフォーカスが外れた時の処理
  const handleFieldBlur = (e) => {
    const { name } = e.target;
    
    // ユーザー名またはアカウント名の場合のみ処理
    if (name === 'username' || name === 'accountname') {
      // フィールドがタッチされた状態にする
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
      
      // エラー表示を有効にする
      setShowFieldErrors(prev => ({
        ...prev,
        [name]: true
      }));
    }
  };

  const handleFieldFocus = (e) => {
    const { name } = e.target;
    
    if (name === 'username' || name === 'accountname') {
      // フォーカス時はエラー表示を一時的に隠す
      setShowFieldErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  // フォーム送信時の処理
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 1. 基本フィールド（ユーザー名、アカウント名）のバリデーション実行
    const validationResult = validateAccountEditForm(formData, isEmailEditable);
    
    // フィールドバリデーションでエラーがある場合
    if (validationResult.hasErrors) {
      // エラー状態を全て更新
      setFieldErrors({
        username: validationResult.username,
        accountname: validationResult.accountname
      });
      
      // エラー表示を有効化
      setShowFieldErrors({
        username: true,
        accountname: true
      });
      
      // タッチ状態を有効化
      setTouchedFields({
        username: true,
        accountname: true
      });
      
      // 最初のエラーメッセージを表示
      const firstError = getFirstErrorMessage(validationResult);
      setError(firstError);
      return;
    }
    
    // 2. パスワードが入力されている場合のみパスワードバリデーション実行
    if (password && password.trim() !== '') {
      const { isValid, errors } = validateForm();
      
      if (!isValid) {
        // パスワード要件エラーの処理
        if (errors.password.length > 0) {
          setError(VALIDATION_MESSAGES.PASSWORD_REQUIREMENTS_NOT_MET);
        } else if (errors.confirmPassword.length > 0) {
          setError(VALIDATION_MESSAGES.PASSWORD_MISMATCH);
        }
        return;
      }
    } else {
      // パスワードが空の場合、確認パスワードも空でなければエラー
      if (confirmPassword && confirmPassword.trim() !== '') {
        setError(VALIDATION_MESSAGES.PASSWORD_MISMATCH);
        return;
      }
    }
    
    // 3. 全てのバリデーションが通った場合、親コンポーネントのonSubmitを呼び出し
    setError(null);
    onSubmit(e);
  };

  // キャンセルボタンが押された時の処理
  const handleCancel = () => {
    navigate(-1); // 前のページに戻る
  };

  return (
    <div className="l-layout__account max-middle l-layout__account-account">
      <figure className="l-layout__account-left">
        <img src={iconAccount} alt="タイトルアイコン" />
      </figure>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form className="account-form l-layout__account-right" onSubmit={handleSubmit}>
        <div className="form-group form-grid">
          <label htmlFor="username" className="form-label">{usernameLabel}</label>
          <div className="form-input-container">
            <input
              type="text"
              id="username"
              name="username"
              className={`p-account-box ${showFieldErrors.username && fieldErrors.username.length > 0 ? 'error' : ''} ${showFieldErrors.username && fieldErrors.username.length === 0 && touchedFields.username ? 'success' : ''}`}
              value={formData.username || ''}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              onFocus={handleFieldFocus}
              disabled={isLoading}
            />
            {/* ユーザー名バリデーションエラー表示 */}
            {showFieldErrors.username && fieldErrors.username.length > 0 && (
              <div className="password-validation-errors">
                {fieldErrors.username.map((error, index) => (
                  <div key={index} className="validation-error">
                    {error}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group form-grid">
          <label htmlFor="accountname" className="form-label account-email-label">
            アカウント名（メールアドレス）
          </label>
          <div className="form-input-container">
            {!isEmailEditable ? (
              <div className="form-readonly-value">
                {formData.accountname || ''}
              </div>
            ) : (
              <>
                <input
                  type="email"
                  id="accountname"
                  name="accountname"
                  className={`p-account-box ${showFieldErrors.accountname && fieldErrors.accountname.length > 0 ? 'error' : ''} ${showFieldErrors.accountname && fieldErrors.accountname.length === 0 && touchedFields.accountname ? 'success' : ''}`}
                  value={formData.accountname || ''}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  onFocus={handleFieldFocus}
                  disabled={isLoading}
                />
                {/* アカウント名（メールアドレス）バリデーションエラー表示 */}
                {showFieldErrors.accountname && fieldErrors.accountname.length > 0 && (
                  <div className="password-validation-errors">
                    {fieldErrors.accountname.map((error, index) => (
                      <div key={index} className="validation-error">
                        {error}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="form-group form-grid">
          <label htmlFor="password" className="form-label">
            パスワード
          </label>
          <div className="form-input-container">
            <input
              type="password"
              id="password"
              name="password"
              className={`p-account-box ${showPasswordErrors ? 'error' : ''} ${isPasswordSecure ? 'success' : ''}`}
              value={password}
              onChange={handleInputChange}
              onBlur={handlePasswordBlur}
              onFocus={handlePasswordFocus}
              placeholder="変更する場合のみ入力"
              disabled={isLoading || isValidating}
            />
            
            {/* パスワードバリデーションエラー表示 */}
            {showPasswordErrors && (
              <div className="password-validation-errors">
                {passwordErrors.map((error, index) => (
                  <div key={index} className="validation-error">
                    {error}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group form-grid">
          <label htmlFor="confirmPassword" className="form-label">再確認</label>
          <div className="form-input-container">
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className={`p-account-box ${showPasswordMatchError ? 'error' : ''} ${isPasswordMatch && confirmPassword ? 'success' : ''}`}
              value={confirmPassword}
              onChange={handleInputChange}
              onBlur={handleConfirmPasswordBlur}
              onFocus={handleConfirmPasswordFocus}
              placeholder="変更する場合のみ入力"
              disabled={isLoading || isValidating}
            />
            {/* パスワード（再確認）バリデーションエラー表示 */}
            {showPasswordMatchError && (
              <div className="validation-error">
                {VALIDATION_MESSAGES.PASSWORD_MISMATCH}
              </div>
            )}
          </div>
        </div>
        
        <div className="button-group button-group--mt-l">
          <button 
            type="button" 
            className="button button-secondary button-large" 
            onClick={handleCancel}
            disabled={isLoading}
          >
            戻る
          </button>
          <button 
            type="submit" 
            className="button button-primary button-large"
            disabled={isLoading || isValidating}
          >
            {isLoading ? '処理中...' : isValidating ? 'チェック中...' : '変更を保存する'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountEditForm;